"use client"

import * as React from "react"
import { BanIcon, EyeIcon, MoreHorizontalIcon, PencilIcon, RotateCcwIcon } from "lucide-react"
import { RoleScope } from "@ibada/shared-types/workspace"

import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EmptyState,
  Skeleton,
  cn,
} from "@ibada/ui"

import { WorkspaceUserStatus } from "@/features/auth/model/auth-types"
import { getActiveDashboardBranchItems, type TableToolbarViews, UserRowAction } from "@/features/users/components/users-table"
import { modulesForScope, resolveModuleAccess } from "@/features/users/lib/module-access"
import { teamMemberDisplayName } from "@/features/users/lib/team-members"
import { WorkspaceUserRole, type WorkspaceTeamMember } from "@/features/users/model"
import { ModuleAccessLevel, TeamPanelSearchParam } from "@/features/users/model/team-panel"
import { useAssignableWorkspaceRolesQuery } from "@/features/users/services/assignable-roles-query"
import { useLocale, useTranslations } from "@/i18n"
import { AliveAvatar, AlivePillTone, AliveStatusCell, formatAliveRelativeDate } from "@/shared/components/alive-cells"
import { useSearchParamSelection } from "@/shared/hooks/use-search-param-selection"
import { useSelectableListKeyboard } from "@/shared/hooks/use-selectable-list-keyboard"

/**
 * The desktop team surface (board M4): the people on the left, the selected person on the right. Every action
 * goes back to the host container through `onAction`, which applies the same guards as the table; this file
 * only lays the data out.
 */

/** The Team / Invitations pills at the head of the list card, each with its count. */
export function TeamPanelViewPills({ views }: { views: TableToolbarViews }) {
  return (
    <div className='flex flex-wrap gap-1 px-1.5 pt-1 pb-2.5' role='tablist'>
      {views.items.map((item) => {
        const active = item.value === views.value
        return (
          <button
            key={item.value}
            type='button'
            role='tab'
            aria-selected={active}
            onClick={() => views.onValueChange(item.value)}
            className={cn(
              "rounded-full px-3 py-1.5 text-[12.5px] font-bold transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring",
              active ? "bg-foreground text-background" : "bg-muted text-muted-foreground hover:text-foreground",
            )}
          >
            {item.label}
            {typeof item.count === "number" ? <span className='ml-1 tabular-nums'>{item.count}</span> : null}
          </button>
        )
      })}
    </div>
  )
}

/** Where the selected person's access is read: a branch's roles, or the organization's. */
export type TeamPanelAccessScope = {
  roleScope: RoleScope
  /** The branch whose roles count (null: organization roles, `branchId: null`). */
  branchId: string | null
  /** Mono workspaces grant their settings through a branch role. */
  includeWorkspaceSettings?: boolean
}

type TeamMembersPanelProps = {
  users: WorkspaceTeamMember[]
  isLoading: boolean
  views?: TableToolbarViews
  canManageRow: (user: WorkspaceTeamMember) => boolean
  isSelf: (user: WorkspaceTeamMember) => boolean
  onAction: (action: UserRowAction, user: WorkspaceTeamMember) => void
  accessScope: TeamPanelAccessScope
}

function sortByName(users: WorkspaceTeamMember[], fallback: string) {
  return [...users].sort((a, b) =>
    teamMemberDisplayName(a, fallback).localeCompare(teamMemberDisplayName(b, fallback), undefined, { sensitivity: "base" }),
  )
}

export function TeamMembersPanel({ users, isLoading, views, canManageRow, isSelf, onAction, accessScope }: TeamMembersPanelProps) {
  const t = useTranslations("users.panel")
  const tTable = useTranslations("users.table")
  const fallbackName = tTable("unknownTeamMember")
  const sorted = React.useMemo(() => sortByName(users, fallbackName), [fallbackName, users])

  // The person in the URL, or the first one: a reload or a shared link opens the same person.
  const [requestedId, selectId] = useSearchParamSelection(TeamPanelSearchParam.Person)
  const selected = sorted.find((user) => user.id === requestedId) ?? sorted[0] ?? null

  // Arrow keys, Home / End, Page Up / Down and typeahead move through the people; there is no full record to
  // open, so Enter does nothing more than the selection already did.
  const list = useSelectableListKeyboard({
    items: sorted,
    getId: (user) => user.id,
    getLabel: (user) => teamMemberDisplayName(user, fallbackName),
    selectedId: selected?.id,
    onSelect: (user) => selectId(user.id),
  })

  return (
    <div className='flex min-w-0 items-start gap-4'>
      <div className='bg-card flex w-[380px] shrink-0 flex-col gap-0.5 rounded-2xl border p-2.5'>
        {views ? <TeamPanelViewPills views={views} /> : null}
        {isLoading ? (
          <div className='flex flex-col gap-2 p-1.5' aria-busy='true'>
            {Array.from({ length: 5 }, (_, index) => (
              <div key={index} className='flex items-center gap-2.5 px-1 py-1.5'>
                <Skeleton className='size-8 rounded-full' />
                <div className='flex flex-1 flex-col gap-1.5'>
                  <Skeleton className='h-3 w-32' />
                  <Skeleton className='h-2.5 w-44' />
                </div>
              </div>
            ))}
          </div>
        ) : sorted.length === 0 ? (
          <p className='text-muted-foreground px-2.5 py-6 text-center text-sm'>{t("empty")}</p>
        ) : (
          <ul className='flex flex-col gap-0.5' aria-label={t("listLabel")} {...list.getListProps()}>
            {sorted.map((user, index) => {
              const name = teamMemberDisplayName(user, fallbackName)
              const active = selected?.id === user.id
              return (
                <li key={user.id} role='presentation'>
                  <button
                    type='button'
                    {...list.getItemProps(user, index)}
                    className={cn(
                      "flex w-full items-center gap-2.5 rounded-[14px] border px-2.5 py-2 text-left transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring",
                      active
                        ? "border-emerald-200 bg-emerald-50 dark:border-emerald-500/30 dark:bg-emerald-500/10"
                        : "hover:bg-muted/60 border-transparent",
                    )}
                  >
                    <AliveAvatar name={name} src={user.avatarUrl ?? user.member?.avatarUrl} />
                    <span className='flex min-w-0 flex-1 flex-col'>
                      <span className='truncate text-[13.5px] font-bold'>{name}</span>
                      <span className='text-muted-foreground truncate text-xs'>{user.email}</span>
                    </span>
                    <AliveStatusCell tone={AlivePillTone.Neutral}>{tTable(`roles.${user.workspaceRole}`)}</AliveStatusCell>
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </div>

      <div className='min-w-0 flex-1'>
        {selected ? (
          <TeamPersonDetail
            key={selected.id}
            user={selected}
            canManage={canManageRow(selected)}
            isSelf={isSelf(selected)}
            onAction={onAction}
            accessScope={accessScope}
          />
        ) : isLoading ? (
          <Skeleton className='h-40 w-full rounded-2xl' />
        ) : (
          <EmptyState title={t("noSelection")} />
        )}
      </div>
    </div>
  )
}

const ACCESS_PILL_TONE: Record<ModuleAccessLevel, AlivePillTone> = {
  [ModuleAccessLevel.Edit]: AlivePillTone.Success,
  [ModuleAccessLevel.View]: AlivePillTone.Warning,
  [ModuleAccessLevel.None]: AlivePillTone.Neutral,
}

function monthYear(value: string | null | undefined, locale: string) {
  if (!value) return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return new Intl.DateTimeFormat(locale, { month: "long", year: "numeric" }).format(date)
}

function TeamPersonDetail({
  user,
  canManage,
  isSelf,
  onAction,
  accessScope,
}: {
  user: WorkspaceTeamMember
  canManage: boolean
  isSelf: boolean
  onAction: (action: UserRowAction, user: WorkspaceTeamMember) => void
  accessScope: TeamPanelAccessScope
}) {
  const t = useTranslations("users.panel")
  const tTable = useTranslations("users.table")
  const tModules = useTranslations("roles.permissionGrid.modules")
  const locale = useLocale()
  const name = teamMemberDisplayName(user, tTable("unknownTeamMember"))
  const isOwner = user.workspaceRole === WorkspaceUserRole.OWNER
  const isSuspended = user.status === WorkspaceUserStatus.Suspended

  // The same role catalog the "Voir l'accès" dialog reads, so this summary and that dialog never disagree.
  const rolesQuery = useAssignableWorkspaceRolesQuery({
    scope: accessScope.roleScope,
    assignmentBranchId: accessScope.branchId ?? undefined,
    enabled: !isOwner && (accessScope.roleScope === RoleScope.Workspace || Boolean(accessScope.branchId)),
  })
  const heldRoleKeys = React.useMemo(
    () => user.roles.filter((role) => role.branchId === accessScope.branchId).map((role) => role.roleKey),
    [accessScope.branchId, user.roles],
  )
  const moduleRows = React.useMemo(
    () =>
      resolveModuleAccess(
        heldRoleKeys,
        rolesQuery.data?.items ?? [],
        modulesForScope(accessScope.roleScope, { includeWorkspaceSettings: accessScope.includeWorkspaceSettings }),
      ),
    [accessScope.includeWorkspaceSettings, accessScope.roleScope, heldRoleKeys, rolesQuery.data?.items],
  )

  const branchItems = getActiveDashboardBranchItems(user, (key, values) => tTable(key as never, values as never))
  const since = monthYear(user.createdAt, locale)
  const lastLogin = user.lastLoginAt ? new Date(user.lastLoginAt) : null
  const meta = [
    user.email,
    since ? t("since", { date: since }) : null,
    user.lastLoginAt === undefined
      ? null
      : lastLogin && !Number.isNaN(lastLogin.getTime())
        ? t("lastLogin", { when: formatAliveRelativeDate(lastLogin, locale) })
        : t("neverLoggedIn"),
  ].filter(Boolean)

  const statusTone = isSuspended ? AlivePillTone.Danger : user.status === WorkspaceUserStatus.Active ? AlivePillTone.Success : AlivePillTone.Warning

  return (
    <div className='flex min-w-0 flex-col gap-3.5'>
      <div className='bg-card flex flex-wrap items-center gap-4 rounded-2xl border px-[22px] py-5'>
        <AliveAvatar name={name} src={user.avatarUrl ?? user.member?.avatarUrl} className='size-14' fallbackClassName='text-[21px]' />
        <div className='min-w-0 flex-1'>
          <div className='flex min-w-0 flex-wrap items-center gap-2'>
            <span className='truncate text-xl font-extrabold'>{name}</span>
            <AliveStatusCell tone={AlivePillTone.Neutral}>{tTable(`roles.${user.workspaceRole}`)}</AliveStatusCell>
            <AliveStatusCell tone={statusTone}>{tTable(`status.${user.status}`)}</AliveStatusCell>
          </div>
          <p className='text-muted-foreground mt-1 text-[13px] break-words'>{meta.join(" · ")}</p>
        </div>
        <div className='flex items-center gap-1.5'>
          {canManage ? (
            <Button variant='outline' className='rounded-full' onClick={() => onAction(UserRowAction.EDIT_ACCESS, user)}>
              {t("changeRole")}
            </Button>
          ) : (
            <Button variant='outline' className='rounded-full' onClick={() => onAction(UserRowAction.VIEW_ACCESS, user)}>
              {tTable("accessSummary.manageAccess")}
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant='ghost' size='icon' className='rounded-full'>
                <span className='sr-only'>{tTable("openActions")}</span>
                <MoreHorizontalIcon />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end'>
              <DropdownMenuItem onSelect={() => onAction(UserRowAction.VIEW_ACCESS, user)}>
                <EyeIcon />
                {tTable("accessSummary.manageAccess")}
              </DropdownMenuItem>
              {canManage ? (
                <>
                  <DropdownMenuItem onSelect={() => onAction(UserRowAction.EDIT_ACCESS, user)}>
                    <PencilIcon />
                    {tTable("actions.editAccess")}
                  </DropdownMenuItem>
                  {isSuspended ? (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onSelect={() => onAction(UserRowAction.RESTORE, user)}>
                        <RotateCcwIcon />
                        {tTable("actions.restore")}
                      </DropdownMenuItem>
                    </>
                  ) : isSelf ? null : (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem variant='destructive' onSelect={() => onAction(UserRowAction.SUSPEND, user)}>
                        <BanIcon />
                        {tTable("actions.suspend")}
                      </DropdownMenuItem>
                    </>
                  )}
                </>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className='bg-card rounded-2xl border px-[22px] py-4'>
        <div className='mb-1.5 flex items-center gap-2'>
          <h3 className='text-[14.5px] font-bold'>{t("canDoHere")}</h3>
          {canManage && !isOwner ? (
            <Button
              variant='outline'
              size='sm'
              className='ml-auto rounded-full'
              onClick={() => onAction(UserRowAction.EDIT_ACCESS, user)}
            >
              <PencilIcon className='size-3.5' />
              {t("editAccess")}
            </Button>
          ) : null}
        </div>
        {isOwner ? (
          <p className='text-muted-foreground py-2 text-[13.5px]'>{t("ownerAccess")}</p>
        ) : rolesQuery.isLoading ? (
          <div className='flex flex-col gap-3 py-2' aria-busy='true'>
            {Array.from({ length: 4 }, (_, index) => (
              <Skeleton key={index} className='h-4 w-full' />
            ))}
          </div>
        ) : (
          <ul className='divide-border/60 divide-y'>
            {moduleRows.map((row) => (
              <li key={row.module} className='flex items-center py-2.5 text-[13.5px]'>
                <span className='flex-1'>{tModules(row.module as never)}</span>
                <AliveStatusCell tone={ACCESS_PILL_TONE[row.level]}>{t(`access.${row.level}`)}</AliveStatusCell>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className='bg-card flex flex-wrap items-center gap-x-2.5 gap-y-1 rounded-2xl border px-[22px] py-3.5 text-[13.5px]'>
        <h3 className='flex-1 font-bold'>{t("branches")}</h3>
        {branchItems.length > 0 ? (
          <span className='flex min-w-0 flex-wrap items-center justify-end gap-x-1.5'>
            <span className='truncate'>{branchItems.map((item) => item.label).join(", ")}</span>
            <span className='text-muted-foreground'>· {t("dashboard")}</span>
          </span>
        ) : (
          <span className='text-muted-foreground'>{tTable("branchAccessSummary.none")}</span>
        )}
      </div>
    </div>
  )
}
